/**
 * ProductTour - Desktop spotlight tour using React Joyride
 * PRD: PRD-product-tour-organisateur.md
 */

import React, { useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { desktopTourSteps } from './tourSteps';
import './ProductTour.css';

interface ProductTourProps {
  isRunning: boolean;
  onComplete: () => Promise<void>;
  onSkip: (stepIndex?: number) => Promise<void>;
}

/**
 * ProductTour - Spotlight tour for desktop organizers
 * Uses React Joyride for guided tour with overlay
 */
const ProductTour: React.FC<ProductTourProps> = ({
  isRunning,
  onComplete,
  onSkip,
}) => {
  // Handle tour callbacks
  const handleCallback = useCallback(async (data: CallBackProps) => {
    const { status, action, type, index } = data;

    // Tour finished (completed all steps)
    if (status === STATUS.FINISHED) {
      await onComplete();
      return;
    }

    // Tour skipped - pass the current step index for analytics
    if (status === STATUS.SKIPPED) {
      await onSkip(index);
      return;
    }

    // Handle close button click (same as skip)
    if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER) {
      await onSkip(index);
    }
  }, [onComplete, onSkip]);

  return (
    <Joyride
      steps={desktopTourSteps}
      run={isRunning}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      disableScrolling={false}
      callback={handleCallback}
      spotlightClicks
      disableOverlayClose={false}
      styles={{
        options: {
          // Design System colors - green primary
          primaryColor: '#258E81',
          zIndex: 10000,
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          textColor: '#2d2d2d',
        },
        tooltip: {
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
        tooltipContent: {
          padding: '8px 0',
        },
        buttonNext: {
          backgroundColor: '#258E81',
          borderRadius: '8px',
          padding: '10px 20px',
          fontWeight: 600,
          fontSize: '14px',
        },
        buttonBack: {
          color: '#717171',
          marginRight: '8px',
        },
        buttonSkip: {
          color: '#717171',
          fontSize: '13px',
        },
        buttonClose: {
          display: 'none',
        },
        spotlight: {
          borderRadius: '8px',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Get Started!',
        next: 'Next',
        skip: 'Skip tour',
      }}
      floaterProps={{
        styles: {
          floater: {
            filter: 'drop-shadow(0 10px 8px rgba(0, 0, 0, 0.04)) drop-shadow(0 4px 3px rgba(0, 0, 0, 0.1))',
          },
        },
      }}
    />
  );
};

export default ProductTour;
